import { TFunction } from "i18next";

// Types
import { IOLLensType, ProgressiveLensType } from "src/services/clinics/types";
import { ExamLensType } from "src/services/exams/types";

// Constants
import { eyeTypeEnums, tintOptions } from "src/constants/enums";

// currentlyBeingViewed comes back per eye ({ leftEye, rightEye }), so each
// eye's lens is read straight off it.
export const isLensViewedByEye = (lens: ExamLensType, eye: string): boolean =>
  Boolean(
    eye === eyeTypeEnums.LEFT
      ? lens.currentlyBeingViewed?.leftEye
      : lens.currentlyBeingViewed?.rightEye
  );

// Whether either eye is viewing the lens.
export const isLensViewed = (lens: ExamLensType): boolean =>
  Boolean(
    lens.currentlyBeingViewed?.leftEye || lens.currentlyBeingViewed?.rightEye
  );

// Whether both eyes are viewing the lens.
export const isLensViewedByBothEyes = (lens: ExamLensType): boolean =>
  Boolean(
    lens.currentlyBeingViewed?.leftEye && lens.currentlyBeingViewed?.rightEye
  );

// The lens the given eye is viewing; undefined when the eye has no lens.
export const getLensForEye = (
  lenses: Array<ExamLensType>,
  eye: string
): ExamLensType | undefined =>
  lenses.find((lens) => isLensViewedByEye(lens, eye));

// IOL's toricCylinderType is a plain integer (or null) field, not a
// numeric-prefix rating like the other lens fields -- entered directly as a
// number input rather than a 1-N button row. Bounds shared between
// AddEditIOLModal and the live-update IOLOptions panel so both stay in sync.
export const IOL_TORIC_CYLINDER = {
  parameterName: "toricCylinderType",
  title: "Cylinder",
  tooltip: "Corrects corneal astigmatism; power in diopters.",
  min: 1,
  max: 7,
} as const;

// The 4 IOL-lens fields that pick one of a fixed severity, sent as-is,
// matching how AddEditIOLModal saves them onto an IOL lens preset. Shared
// between that modal and the live-update IOLOptions panel so both stay in
// sync -- same sharing rationale as IOL_TORIC_CYLINDER above.
export const IOL_ENUM_FIELDS = [
  {
    parameterName: "halosType",
    title: "Halos",
    tooltip: "Rings or circles around lights at night.",
    options: ["None", "Mild", "Moderate", "Severe"],
  },
  {
    parameterName: "starburstsType",
    title: "Starbursts",
    tooltip: "Rays or streaks radiating from bright lights.",
    options: ["None", "Mild", "Moderate", "Severe"],
  },
  {
    parameterName: "glaresType",
    title: "Glare",
    tooltip:
      "Diffuse light scatter around bright sources that makes nearby objects harder to see.",
    options: ["None", "Mild", "Moderate", "Severe"],
  },
  {
    parameterName: "reducedContrastType",
    title: "Reduced Contrast",
    tooltip: "Images look slightly washed out, especially in dim light.",
    options: ["None", "Mild", "Moderate", "Severe"],
  },
] as const;

// The remaining progressive-lens fields that pick one of a fixed set of
// named options -- sent as-is, matching how AddEditProgressiveModal saves
// them onto a progressive lens preset. Shared between that modal and the
// live-update ProgressiveOptions panel so both stay in sync. (indexType is
// not here -- its options come from useProgressiveIndexTypes, not a fixed
// list, so each consumer fetches and renders it the same way it handles
// lensType.)
export const PROGRESSIVE_ENUM_FIELDS = [
  {
    parameterName: "corridorLengthType",
    title: "Corridor Length",
    tooltip:
      "Distance between the distance and reading zones. Short fits smaller frames but needs a larger eye movement change; long gives a more gradual transition but needs a taller frame.",
    options: ["Short", "Medium", "Long"],
  },
  {
    parameterName: "corridorWidthType",
    title: "Corridor Width",
    tooltip:
      "Width of the clear zone for intermediate and near vision. Wider means more head-free side-to-side viewing, such as across a computer screen.",
    options: ["Narrow", "Medium", "Wide"],
  },
  {
    parameterName: "depthDistortType",
    title: "Peripheral Distortion (Swim)",
    tooltip:
      "Blur and warping at the sides of the lens, most noticeable when turning the head or walking. Lower is easier to adapt to.",
    options: ["None", "Mild", "Moderate", "Severe"],
  },
] as const;

export const getIOLTooltipText = (lensData: IOLLensType, t: TFunction) => {
  const rows = [
    {
      label: t("Halos"),
      value: lensData?.halosType ? t(lensData.halosType) : "N/A",
    },
    {
      label: t("Starbursts"),
      value: lensData?.starburstsType ? t(lensData.starburstsType) : "N/A",
    },
    {
      label: t("Glare"),
      value: lensData?.glaresType ? t(lensData.glaresType) : "N/A",
    },
    {
      label: t("Reduced Contrast"),
      value: lensData?.reducedContrastType
        ? t(lensData.reducedContrastType)
        : "N/A",
    },
    {
      label: t("Toric Cylinder"),
      value:
        lensData?.toricCylinderType !== null &&
        lensData?.toricCylinderType !== undefined
          ? String(lensData.toricCylinderType)
          : "N/A",
    },
  ];

  return rows.map(({ label, value }) => `${label}: ${value}`).join("\n");
};

export const getProgressiveTooltipText = (
  lensData: ProgressiveLensType,
  t: TFunction
) => {
  const formattedTintOptions = Object.values(tintOptions).map((tint) => ({
    displayName: t(tint.label),
    value: tint.value,
  }));

  const tintName =
    lensData?.tintColor && lensData?.tintColor !== "Undefined"
      ? formattedTintOptions.find((tint) => lensData?.tintColor === tint.value)
          ?.displayName ?? "Custom"
      : undefined;

  const rows = [
    {
      label: t("Index Type"),
      value: lensData?.indexType ? t(lensData.indexType) : "N/A",
    },
    {
      label: t("Corridor Length"),
      value: lensData?.corridorLengthType
        ? t(lensData?.corridorLengthType)
        : "N/A",
    },
    {
      label: t("Corridor Width"),
      value: lensData?.corridorWidthType
        ? t(lensData?.corridorWidthType)
        : "N/A",
    },
    {
      label: t("Peripheral Distortion (Swim)"),
      value: lensData?.depthDistortType ? t(lensData.depthDistortType) : "N/A",
    },
    {
      label: t("Blue Light Filter"),
      value: lensData?.blueLightFilter ? t("On") : t("Off"),
    },
    {
      label: t("Lens Tint"),
      value:
        tintName &&
        lensData?.tintDensity &&
        lensData?.tintDensity !== "Undefined"
          ? `${t(tintName)} - ${t(lensData?.tintDensity)}`
          : "N/A",
    },
  ];

  return rows.map(({ label, value }) => `${label}: ${value}`).join("\n");
};

export const formatLensPrice = (
  price: string,
  currencySymbol: string | undefined,
  currencyCode: string | undefined
) => {
  const priceRegex = /^[0-9.]+$/;

  if (priceRegex.test(price) && currencySymbol && currencyCode) {
    return `${currencySymbol}${price} ${currencyCode}`;
  }

  return price;
};
